import React, { useEffect, useState, forwardRef, useImperativeHandle } from 'react';
import styled from 'styled-components';
import { palette } from 'styled/common';
import { Col, Container, GridThemeProvider, media, Row } from 'styled-bootstrap-grid';
import { IService } from 'interfaces/service.interface';
import moment from 'moment';
import { LoaderOverlay, useSnackbar } from '@components/common';
import { api } from 'helpers/auth-axios';
import DeleteModel from '@components/common/DeleteModel/DeleteModel';
import { IWaitlist } from 'interfaces/waitlist.interface';
import { IServiceProvider } from 'interfaces/team/serviceProvider.interface';
import { WaitlistModal } from '@components/waitlist';
import { ICustomer } from 'interfaces/customer.interface';
import { useMediaQuery } from 'react-responsive';

interface IProps {
    initialView?: string;
    setView?: any;
    serviceProviderId?: any;
    setDate(_date: any): void;
    date: any;
    setDateRange(_dateRange: any): void;
    dateRange: any;
}

const gridTheme = {
    container: {
        maxWidth: {
            xxl: 720,
            xl: 720,
            lg: 720,
            md: 720,
            sm: 720,
            xs: 720,
        },
    },
};

const WaitlistView = forwardRef(
    (
        { initialView, setView, serviceProviderId, setDate, date, setDateRange, dateRange }: IProps,
        ref
    ) => {
        useImperativeHandle(ref, () => ({
            next() {
                next();
            },
            prev() {
                prev();
            },
            today() {
                today();
            },
            changeView(view?: string) {
                changeView(view);
            },
        }));

        const [loading, setLoading] = useState(false);
        const [openSnackbar] = useSnackbar();
        const [waitlistEntries, setWaitlistEntries] = useState<IWaitlist[]>([]);
        const [showAddModal, setShowAddModal] = useState<boolean>(false);
        const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
        const [entryToDelete, setEntryToDelete] = useState<IWaitlist | undefined>();

        const isMobile = useMediaQuery({ query: '(max-width: 760px)' });

        const next = () => {
            const _date = moment(date).startOf('week').add(1, 'week');
            setDate(_date);
            setDateRange(formatDateRangeForWeek(_date));
        };

        const prev = () => {
            const _date = moment(date).startOf('week').subtract(1, 'week');
            setDate(_date);
            setDateRange(formatDateRangeForWeek(_date));
        };

        const today = () => {
            const _date = moment().startOf('week');
            setDate(_date);
            setDateRange(formatDateRangeForWeek(_date));
        };

        const changeView = (view?: string) => {};

        const openDeleteModal = (entry: IWaitlist) => {
            setEntryToDelete(entry);
            setShowDeleteModal(true);
        };

        const formatDateRangeForWeek = (targetDate: moment.Moment) => {
            const weekStart = targetDate.clone().startOf('week');
            const weekEnd = targetDate.clone().endOf('week');
            if (weekStart.year() != weekEnd.year()) {
                return `${weekStart.format('MMM D, YYYY')} - ${weekEnd.format('MMM D, YYYY')}`;
            } else if (weekStart.month() != weekEnd.month()) {
                return `${weekStart.format('MMM D')} - ${weekEnd.format('MMM D, YYYY')}`;
            } else {
                return `${weekStart.format('MMM D')} - ${weekEnd.format('D, YYYY')}`;
            }
        };

        useEffect(() => {
            const _date = moment(date).startOf('week');
            setDate(_date);

            setDateRange(formatDateRangeForWeek(_date));
        }, []);

        useEffect(() => {
            getWaitlistEntries();
        }, [serviceProviderId, date]);

        const getWaitlistEntries = () => {
            if (!serviceProviderId || !date) {
                return;
            }

            setLoading(true);
            api.get(
                `/waitlist/${serviceProviderId}?fromDate=${moment(date)
                    .clone()
                    .startOf('week')
                    .format('YYYY-MM-DD')}&tillDate=${moment(date)
                    .clone()
                    .endOf('week')
                    .format('YYYY-MM-DD')}`
            )
                .then((res: any) => {
                    let entries = res.data;
                    // sort the waitlist entries by preferred_date_time then created_at
                    entries.sort((a: IWaitlist, b: IWaitlist) => {
                        const PDa = moment(a.preferred_date_time);
                        const PDb = moment(b.preferred_date_time);
                        const CAa = moment(a.created_at);
                        const CAb = moment(b.created_at);
                        const SORT_A_BEFORE_B = -1;
                        const SORT_B_BEFORE_A = 1;
                        const KEEP_SAME = 0;
                        if (PDa.isBefore(PDb)) {
                            return SORT_A_BEFORE_B;
                        } else if (PDb.isBefore(PDa)) {
                            return SORT_B_BEFORE_A;
                        } else {
                            if (CAa.isBefore(CAb)) {
                                return SORT_A_BEFORE_B;
                            } else if (CAb.isBefore(CAa)) {
                                return SORT_B_BEFORE_A;
                            } else {
                                return KEEP_SAME;
                            }
                        }
                    });
                    setWaitlistEntries(entries);
                    setLoading(false);
                })
                .catch((e: any) => {
                    setLoading(false);
                    if (e?.response) {
                        openSnackbar(e.response?.data?.message);
                    }
                });
        };

        const deleteWaitlistEntry = (entry: IWaitlist) => {
            setLoading(true);
            api.delete(`/waitlist/${serviceProviderId}/${entry._id}`)
                .then((res: any) => {
                    openSnackbar('Successfully deleted waitlist entry!');
                    getWaitlistEntries();
                })
                .catch((e: any) => {
                    setLoading(false);
                    if (e?.response) {
                        openSnackbar(e.response?.data?.message);
                    }
                });
        };

        const formatPhoneNumberForSmsLink = (phoneNumber: string) => {
            if (!phoneNumber) return '';
            // customer phone numbers are stored in format 012-345-6789
            // we want them in the format +10123456789
            return `+1${phoneNumber.replaceAll('-', '')}`;
        };
        const formatPhoneNumberForTelLink = (phoneNumber: string) => {
            if (!phoneNumber) return '';
            // customer phone numbers are stored in format 012-345-6789
            // we want them in the format 10123456789
            return `1${phoneNumber.replaceAll('-', '')}`;
        };

        return (
            <>
                <GridThemeProvider gridTheme={gridTheme}>
                    <Wrapper>
                        {loading && <LoaderOverlay />}
                        <Container>
                            <HeaderRow>
                                {isMobile ? (
                                    <CenterCol xs={12}>
                                        <WaitlistHeader>Waitlist: {dateRange}</WaitlistHeader>
                                    </CenterCol>
                                ) : (
                                    <LeftCol xs={12}>
                                        <WaitlistHeader>Waitlist</WaitlistHeader>
                                    </LeftCol>
                                )}
                            </HeaderRow>
                            {waitlistEntries !== undefined &&
                                waitlistEntries.map((entry: IWaitlist, i: any) => (
                                    <div key={i}>
                                        <EntryRow>
                                            <Col xs={12}>
                                                <Row>
                                                    <LeftCol xs={12} sm={12}>
                                                        <NameDiv>
                                                            {entry.firstname} {entry.lastname}
                                                        </NameDiv>
                                                        <EmailDiv>{entry.email}</EmailDiv>
                                                        <PhoneDiv>{entry.phone}</PhoneDiv>
                                                        <CreatedDate>
                                                            Requested On:{' '}
                                                            {moment(entry.created_at).format(
                                                                'ddd, MMMM D YYYY [at] h:mm a'
                                                            )}
                                                        </CreatedDate>
                                                        <ServiceTitle>Service</ServiceTitle>
                                                        <ServiceName>
                                                            {(entry.service as IService).name}
                                                        </ServiceName>
                                                        <ServiceTitle>
                                                            Service Provider
                                                        </ServiceTitle>
                                                        <ServiceName>
                                                            {entry.service_provider
                                                                ? (
                                                                      entry.service_provider as IServiceProvider
                                                                  ).name
                                                                : `Anyone`}
                                                        </ServiceName>
                                                        <PreferredRangeTitle>
                                                            Time Preference
                                                        </PreferredRangeTitle>
                                                        <PreferredRange>
                                                            {entry.preferred_range}
                                                        </PreferredRange>
                                                        <PreferredRangeTitle>
                                                            Date Preference
                                                        </PreferredRangeTitle>
                                                        <PreferredRange>
                                                            {moment(
                                                                entry.preferred_date_time
                                                            ).format('ddd MMM D, YYYY')}
                                                        </PreferredRange>
                                                        {entry.note && (
                                                            <>
                                                                <NotesTitle>Notes</NotesTitle>
                                                                <NotesText>{entry.note}</NotesText>
                                                            </>
                                                        )}
                                                    </LeftCol>
                                                </Row>
                                                <ButtonRow>
                                                    <SmsButton
                                                        href={`sms:${formatPhoneNumberForSmsLink(
                                                            (entry.customer as ICustomer)?.phone
                                                        )}`}>
                                                        Text
                                                    </SmsButton>
                                                    <PhoneButton
                                                        href={`tel:${formatPhoneNumberForTelLink(
                                                            (entry.customer as ICustomer)?.phone
                                                        )}`}>
                                                        Call
                                                    </PhoneButton>
                                                    <EmailButton
                                                        href={`mailto:${
                                                            (entry.customer as ICustomer)?.email ??
                                                            ''
                                                        }`}>
                                                        Email
                                                    </EmailButton>
                                                    <DeleteButton
                                                        onClick={() => openDeleteModal(entry)}>
                                                        Remove
                                                    </DeleteButton>
                                                </ButtonRow>
                                            </Col>
                                        </EntryRow>
                                    </div>
                                ))}
                            <AddButton onClick={() => setShowAddModal(true)}>
                                <i className="fal fa-plus"></i>
                            </AddButton>
                        </Container>
                    </Wrapper>
                </GridThemeProvider>
                {showAddModal && (
                    <WaitlistModal
                        initialServiceProviderId={serviceProviderId}
                        initialDate={date}
                        onClose={() => {
                            getWaitlistEntries();
                            setShowAddModal(false);
                        }}
                    />
                )}
                {showDeleteModal && (
                    <DeleteModel
                        id={''}
                        onDelete={() => {}}
                        onClose={() => setShowDeleteModal(false)}
                        heading="Remove from Waitlist?"
                        content="If you click Remove, the client will be removed from the waitlist for the selected date."
                        buttonText="Remove"
                        loading={loading}
                        onCLick={() => {
                            if (entryToDelete) {
                                deleteWaitlistEntry(entryToDelete);
                            }
                            setEntryToDelete(undefined);
                            setShowDeleteModal(false);
                        }}
                    />
                )}
            </>
        );
    }
);
const Wrapper = styled.div`
    flex-direction: column !important;
    flex: 1 1 0% !important;
    display: flex !important;
`;
const HeaderRow = styled(Row)`
    padding-top: 6px;
    padding-bottom: 12px;
`;
const WaitlistHeader = styled.span`
    font-size: 1.5rem;
    ${media.sm`
        font-size: 2rem;
    `}
    font-weight: 600;
    color: #0006;
`;
const EntryRow = styled(Row)`
    border-top: 0.1rem solid ${palette.grey.lightest};
    padding: 1.375rem 0;
`;
const LeftCol = styled(Col)`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: start;
`;
const CenterCol = styled(Col)`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
`;
const RightCol = styled(Col)`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: end;
`;
const NameDiv = styled.div`
    font-size: 1.6rem;
    line-height: 2rem;
    font-weight: 600;
`;
const EmailDiv = styled.div`
    font-size: 0.875rem;
    line-height: 1.25rem;
    color: rgb(125, 128, 131);
`;
const PhoneDiv = styled.div`
    font-size: 0.875rem;
    line-height: 1.25rem;
    color: rgb(125, 128, 131);
`;
const CreatedDate = styled.div`
    font-size: 0.875rem;
    line-height: 1.25rem;
    color: rgb(125, 128, 131);
`;
const ServiceTitle = styled.div`
    font-size: 0.875rem;
    font-weight: 600;
`;
const ServiceName = styled.div`
    font-size: 0.875rem;
`;
const PreferredRangeTitle = styled.div`
    font-size: 0.875rem;
    font-weight: 600;
`;
const PreferredRange = styled.div`
    font-size: 0.875rem;
    text-transform: capitalize;
`;
const NotesTitle = styled.div`
    font-weight: 600;
    font-size: 0.875rem;
`;
const NotesText = styled.div`
    color: rgb(125, 128, 131);
    font-size: 0.875rem;
`;
const AddButton = styled.div`
    position: fixed;
    bottom: 1.71rem;
    width: 3.42rem;
    height: 3.42rem;
    display: flex;
    justify-content: center;
    align-items: center;
    box-shadow: 0 1rem 1.71rem 0 rgb(0 0 0 / 28%);
    background: #0154ff;
    border-radius: 100%;
    color: #fff;
    font-size: 1.5rem;
`;
const ButtonRow = styled.div`
    width: 100%;
    padding: 1.275rem 0 0.375rem;
    display: flex;
    justify-content: start;
    align-items: center;
    gap: 14px 0;
    flex-wrap: wrap;
    ${media.sm`
        padding: 1.375rem 0 0.375rem;
        gap: 12px;
    `}
`;
const BasicButton = styled.button`
    min-width: 5rem;
    min-height: 2.85rem;
    margin-right: 0.71rem;
    padding: 0.85rem 1.5rem;
    color: ${palette.light};
    font-weight: 600;
    font-size: 1.07rem;
    border-width: 0rem;
    border-radius: 0.51rem;
    background-color: rgb(187, 194, 202);
    cursor: pointer;
    &:hover {
        background-color: rgb(172, 180, 190);
    }
`;
const BasicLink = styled.a`
    min-width: 5rem;
    min-height: 2.85rem;
    margin-right: 0.71rem;
    padding: 0.8rem 1.5rem;
    color: ${palette.light};
    font-weight: 600;
    font-size: 1.07rem;
    border-width: 0rem;
    border-radius: 0.51rem;
    background-color: #000;
    cursor: pointer;
    &:hover {
        background-color: #000b;
    }
`;
const SmsButton = styled(BasicLink)`
    ${media.xs`
        min-width: 2rem;
        padding: 0.8rem 1rem;
    `}
`;
const PhoneButton = styled(BasicLink)`
    ${media.xs`
        min-width: 2rem;
        padding: 0.8rem 1.2rem;
    `}
`;
const EmailButton = styled(BasicLink)`
    ${media.xs`
        min-width: 2rem;
        padding: 0.8rem 1rem;
    `}
`;
const DeleteButton = styled(BasicButton)`
    background-color: ${palette.primary};
    &:hover {
        background-color: ${palette.primaryHover};
    }
`;
export default WaitlistView;
